import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';

import { CharityPartner } from '../../models/content';
import { FileService } from '../../services/file.service';
import { ModalService } from '../../services/modal.service';
import { contentSlug, CONTENT_SLUG_PATTERN } from '../../content-slug';

/**
 * Charity partner editor — opened from ContentCharityListComponent. Returns the
 * mutated CharityPartner via dialogRef.close; the list writes it back + persists
 * via accountService.patchContent('charities', ...).
 *
 * Cloned from ContentRegionComponent (same slug auto-gen pattern), minus `state`,
 * plus `description` (Markdown), `url`, and a native logo upload — the logo rides
 * on the item as `logoFileId` (CHARITY-PARTNERS.md §4 Pattern B), uploaded via the
 * shared file-upload modal.
 */
@Component({
  selector: 'app-content-charity',
  templateUrl: './content-charity.component.html',
})
export class ContentCharityComponent implements OnInit {
  charity!: CharityPartner;
  isNew = false;
  /** True while the save request is in flight — Save spins, Cancel locks. */
  saving = false;
  form!: FormGroup;
  slugIsReadOnly = true;
  logoFileId: number | null = null;
  logoPreview: string | null = null;
  heroFileId: number | null = null;
  heroPreview: string | null = null;

  constructor(
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private fb: FormBuilder,
    private modalService: ModalService,
    private fileService: FileService
  ) {}

  ngOnInit(): void {
    this.charity = this.config.data.charity;
    this.isNew = !!this.config.data.isNew;
    this.logoFileId = typeof this.charity.logoFileId === 'number' ? this.charity.logoFileId : null;
    this.heroFileId = typeof this.charity.heroFileId === 'number' ? this.charity.heroFileId : null;
    // Fresh signed preview URL, if the list resolved one; otherwise resolve it
    // from the saved fileId so the thumbnail shows on re-edit (also confirms the
    // S3 object is a valid image).
    this.logoPreview = this.config.data.logoPreview ?? null;
    if (this.logoFileId != null && !this.logoPreview) {
      this.fileService.getPreviewLink(String(this.logoFileId)).subscribe({
        next: (url) => (this.logoPreview = url),
        error: () => {},
      });
    }
    this.heroPreview = this.config.data.heroPreview ?? null;
    if (this.heroFileId != null && !this.heroPreview) {
      this.fileService.getPreviewLink(String(this.heroFileId)).subscribe({
        next: (url) => (this.heroPreview = url),
        error: () => {},
      });
    }

    this.form = this.fb.group({
      name: new FormControl(this.charity.name ?? '', [Validators.required]),
      slug: new FormControl(this.charity.slug ?? '', [
        Validators.required,
        Validators.pattern(CONTENT_SLUG_PATTERN),
      ]),
      intro: new FormControl(this.charity.intro ?? ''),
      description: new FormControl(this.charity.description ?? ''),
      url: new FormControl(this.charity.url ?? ''),
      order: new FormControl(this.charity.order ?? null),
      published: new FormControl(this.charity.published !== false),
    });

    this.slugIsReadOnly = (this.charity.slug || '') === contentSlug(this.charity.name || '');
  }

  onNameInput(): void {
    if (this.slugIsReadOnly) {
      this.form.get('slug')!.setValue(contentSlug(this.form.get('name')!.value || ''));
    }
  }

  toggleSlugEdit(): void {
    this.slugIsReadOnly = !this.slugIsReadOnly;
    if (this.slugIsReadOnly) {
      this.form.get('slug')!.setValue(contentSlug(this.form.get('name')!.value || ''));
    }
  }

  /** Upload a logo via the shared file-upload modal; keep the returned fileId. */
  uploadLogo(): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload charity logo',
      bucketPrefix: 'charity-logos',
      // Raster only — the site's asset globs are {jpg,jpeg,png,webp,avif}, so a
      // GIF or SVG uploads and syncs but silently renders nothing.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      this.logoFileId = file.id;
      if (file.link) this.logoPreview = file.link;
    });
  }

  removeLogo(): void {
    this.logoFileId = null;
    this.logoPreview = null;
  }

  uploadHero(): void {
    const ref = this.modalService.fileUpload({
      title: 'Upload charity hero image',
      bucketPrefix: 'charity-heroes',
      // Raster only — the site's asset globs are {jpg,jpeg,png,webp,avif}, so a
      // GIF or SVG uploads and syncs but silently renders nothing.
      accept: 'image/jpeg,image/png,image/webp,image/avif',
      multiple: false,
    });
    ref.onClose.subscribe((result?: { files: { id: number; link?: string }[] }) => {
      const file = result?.files?.[0];
      if (!file) return;
      this.heroFileId = file.id;
      if (file.link) this.heroPreview = file.link;
    });
  }

  removeHero(): void {
    this.heroFileId = null;
    this.heroPreview = null;
  }

  onSave(): void {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = { ...this.form.value };
    value.slug = contentSlug(value.slug || '');
    value.order = value.order === null || value.order === '' ? null : Number(value.order);
    value.intro = (value.intro || '').trim() || null;
    value.description = (value.description || '').trim() || null;
    value.url = (value.url || '').trim() || null;
    // null explicitly clears a removed logo in the EAV; a number sets it.
    value.logoFileId = this.logoFileId;
    value.heroFileId = this.heroFileId;
    Object.assign(this.charity, value);
    // Save-then-close: the dialog stays open (Save spinning) until the server
    // confirms, so a publish cannot race an in-flight write; on rejection it
    // stays open with everything typed rather than losing the edit.
    const save = this.config.data.save as ((v: any) => any) | undefined;
    if (!save) {
      this.ref.close({ saved: true, charity: this.charity });
      return;
    }
    this.saving = true;
    save(this.charity).subscribe({
      next: () => this.ref.close({ saved: true, charity: this.charity, persisted: true }),
      error: (err: any) => {
        this.saving = false;
        this.modalService.error(err?.error?.error || err);
      },
    });
  }

  onCancel(): void {
    this.ref.close({ saved: false });
  }
}
